import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyStats, getMyLeagues, getNextEvent, getLastCompletedEvent, getCompletedEventsWithWinners, deleteAccount } from '../services/api';
import { isChatAlertsMuted, setChatAlertsMuted } from '../utils/chatAlerts';
import { COUNTRIES, getCountryFlag, getCountryName } from '../utils/countries';
import { BADGE_CATEGORIES, isEarned, getEarnedCount } from '../utils/badgeDefinitions';
import PullToRefresh from '../components/PullToRefresh';
import BadgeDetailModal, { BadgeMedallion, getBadgeProgress } from '../components/BadgeDetailModal';
import OctaAvatar from '../components/OctaAvatar';
import { HIDE_MONEY } from '../utils/platform';
import '../styles/app/profile.css';

function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('career'); // 'career' | 'activity' | 'settings'

  // Stats
  const [stats, setStats] = useState({
    totalPicks: 0,
    badges: [],
    poolsJoined: 0,
    eventsPicked: 0,
    correctPicks: 0,
    scoredPicks: 0,
    accuracy: 0
  });
  const [pools, setPools] = useState([]);
  const [nextEvent, setNextEvent] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Tapped badge for detail modal
  const [selectedBadge, setSelectedBadge] = useState(null);

  // Previous events (per pool)
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [poolEvents, setPoolEvents] = useState([]);
  const [poolEventsLoading, setPoolEventsLoading] = useState(false);

  // Settings form
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [venmoHandle, setVenmoHandle] = useState(user?.venmo_handle || '');
  const [country, setCountry] = useState(user?.country || '');
  const [instagram, setInstagram] = useState(user?.instagram || '');
  const [xHandle, setXHandle] = useState(user?.x_handle || '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Delete-account flow
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Notification preferences
  const [chatAlertsOn, setChatAlertsOn] = useState(!isChatAlertsMuted());

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== user?.username) {
      setDeleteError('Username does not match.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount();
      // Clear local session and bounce to home
      try { await logout(); } catch (_) {}
      window.location.href = '/';
    } catch (e) {
      setDeleteError(e.response?.data?.error || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const fetchAll = async () => {
    try {
      const [statsRes, poolsRes, nextRes, lastRes] = await Promise.all([
        getMyStats(),
        getMyLeagues().catch(() => ({ data: [] })),
        getNextEvent().catch(() => ({ data: null })),
        getLastCompletedEvent().catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      const allPools = poolsRes.data || [];
      setPools(allPools);
      setNextEvent(nextRes.data);
      setLastEvent(lastRes.data);
      // Default-select first pool for the Previous Events viewer if none chosen
      if (!selectedPoolId && allPools.length > 0) {
        setSelectedPoolId(String(allPools[0].id));
      }
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedPoolId) {
      setPoolEvents([]);
      return;
    }
    let cancelled = false;
    setPoolEventsLoading(true);
    getCompletedEventsWithWinners(selectedPoolId)
      .then(res => { if (!cancelled) setPoolEvents(res.data || []); })
      .catch(() => { if (!cancelled) setPoolEvents([]); })
      .finally(() => { if (!cancelled) setPoolEventsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedPoolId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const updateData = {};
      if (firstName.trim() !== (user?.first_name || '')) updateData.firstName = firstName.trim();
      if (lastName.trim() !== (user?.last_name || '')) updateData.lastName = lastName.trim();
      if (username.trim() !== user?.username) updateData.username = username.trim();
      // Native hides the Venmo field: never send it, so the stored handle is kept.
      if (!HIDE_MONEY && venmoHandle.trim() !== (user?.venmo_handle || '')) updateData.venmoHandle = venmoHandle.trim();
      if (country !== (user?.country || '')) updateData.country = country;
      if (instagram.trim() !== (user?.instagram || '')) updateData.instagram = instagram.trim();
      if (xHandle.trim() !== (user?.x_handle || '')) updateData.xHandle = xHandle.trim();

      if (Object.keys(updateData).length === 0) {
        setSuccess('No changes to save');
        setSaving(false);
        return;
      }

      await updateProfile(updateData);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const formatJoinDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const titleShotBadges = stats.badges.filter(b => b.badge_type === 'title_shot_winner');
  const whiteHouseBadges = stats.badges.filter(b => b.badge_type === 'white_house_belt');
  const totalBadges = stats.badges.length;

  // Rank progression based on total picks made
  const RANKS = [
    { name: 'Walk-On',       icon: '🥋', min: 0,    max: 25 },
    { name: 'Prospect',      icon: '🥊', min: 25,   max: 100 },
    { name: 'Contender',     icon: '🥊', min: 100,  max: 250 },
    { name: 'Ranked',        icon: '🏆', min: 250,  max: 500 },
    { name: '#1 Contender',  icon: '🏆', min: 500,  max: 1000 },
    { name: 'Title Holder',  icon: '👑', min: 1000, max: 2500 },
    { name: 'Champion',      icon: '👑', min: 2500, max: 5000 },
    { name: 'GOAT',          icon: '🐐', min: 5000, max: Infinity },
  ];
  const currentRank = RANKS.find(r => stats.totalPicks >= r.min && stats.totalPicks < r.max) || RANKS[0];
  const isMaxRank = currentRank.max === Infinity;
  const rankProgress = isMaxRank
    ? 100
    : Math.min(100, Math.round(((stats.totalPicks - currentRank.min) / (currentRank.max - currentRank.min)) * 100));
  const picksToNext = isMaxRank ? 0 : currentRank.max - stats.totalPicks;

  const hasTitleBelt = titleShotBadges.length > 0;
  const statValue = (v) => (statsLoading ? '—' : v);

  return (
    <PullToRefresh onRefresh={fetchAll}>
    <div className="container pf-page">
      <div className="pf-layout">
        <aside className="pf-side">
          {/* HEADER: Avatar + username + member since */}
          <section className={`pf-hero profile-header pnp-rise ${hasTitleBelt ? 'pf-hero-champ' : ''}`}>
            <div className={`pnp-claw ${hasTitleBelt ? 'pnp-claw-gold' : ''} pf-hero-claw`} aria-hidden="true" />
            <div className="pf-hero-glow" aria-hidden="true" />

            <div className={`pf-avatar-glow ${hasTitleBelt ? 'pf-avatar-glow-gold' : ''}`}>
              <div className={`pf-avatar-frame ${hasTitleBelt ? 'pf-avatar-frame-gold' : ''}`}>
                <div className="pf-avatar-inner">
                  <OctaAvatar
                    name={user?.username}
                    size={112}
                    ring={hasTitleBelt}
                    ringColor="#D4A017"
                  />
                </div>
              </div>
            </div>

            <p className={`pnp-eyebrow ${hasTitleBelt ? 'pnp-eyebrow-gold' : ''} pf-hero-eyebrow`}>
              {hasTitleBelt ? 'Title Shot Champion' : 'Fighter Profile'}
            </p>
            <h2 className="pnp-title pf-name">
              <span className="pf-name-text">{user?.username}</span>
              {(titleShotBadges.length > 0 || whiteHouseBadges.length > 0) && (
                <span className="pf-name-belts">
                  {titleShotBadges.length > 0 && (
                    <span className="pf-belt">
                      <img src="/images/title-belt.svg" alt="Title Shot Winner" />
                      {titleShotBadges.length > 1 && (
                        <span className="pf-belt-count">
                          x{titleShotBadges.length}
                        </span>
                      )}
                    </span>
                  )}
                  {whiteHouseBadges.length > 0 && (
                    <span className="pf-belt" title="White House Belt — UFC Freedom 250 winner">
                      <img src="/images/white-house-belt.svg" alt="White House Belt" />
                    </span>
                  )}
                </span>
              )}
            </h2>
            <p className="pf-meta">
              Member since {formatJoinDate(user?.created_at)}
              {user?.country && (
                <span> · {getCountryFlag(user.country)} {getCountryName(user.country)}</span>
              )}
            </p>

            {/* Social links */}
            {(user?.instagram || user?.x_handle) && (
              <div className="pf-socials">
                {user.instagram && (
                  <a href={`https://instagram.com/${user.instagram}`} target="_blank" rel="noopener noreferrer" className="pf-social">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    {user.instagram}
                  </a>
                )}
                {user.x_handle && (
                  <a href={`https://x.com/${user.x_handle}`} target="_blank" rel="noopener noreferrer" className="pf-social">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    {user.x_handle}
                  </a>
                )}
              </div>
            )}

            {/* Quick stats row */}
            <div className="pnp-stats pf-stats">
              <div className="pnp-stat">
                <span className="pnp-stat-value">{statValue(stats.poolsJoined)}</span>
                <span className="pnp-stat-label">Pools</span>
              </div>
              <div className="pnp-stat">
                <span className="pnp-stat-value">{statValue(stats.eventsPicked)}</span>
                <span className="pnp-stat-label">Events</span>
              </div>
              <div className="pnp-stat">
                <span className="pnp-stat-value">{statValue(stats.totalPicks.toLocaleString())}</span>
                <span className="pnp-stat-label">Picks</span>
              </div>
              <div className="pnp-stat">
                <span className="pnp-stat-value">{statValue(`${stats.accuracy}%`)}</span>
                <span className="pnp-stat-label">Accuracy</span>
              </div>
            </div>
          </section>

          {/* RANK PROGRESSION */}
          <section className="pf-card pf-rank pnp-rise" style={{ '--i': 1 }}>
            <div className="pf-rank-row">
              <div className="pf-rank-icon" aria-hidden="true">
                {currentRank.icon}
              </div>
              <div className="pf-rank-text">
                <p className="pf-rank-eyebrow">Rank</p>
                <p className="pnp-title pf-rank-name">{currentRank.name}</p>
                <p className="pf-rank-count">
                  {isMaxRank
                    ? `${stats.totalPicks.toLocaleString()} picks · Maxed out`
                    : `${stats.totalPicks.toLocaleString()} / ${currentRank.max.toLocaleString()} picks`}
                </p>
              </div>
              {!isMaxRank && (
                <span className="pnp-chip pnp-chip-red pf-rank-chip">
                  {picksToNext} to go
                </span>
              )}
            </div>
            <div className="pf-bar">
              <div className="pf-bar-fill" style={{ width: `${rankProgress}%` }} />
            </div>
          </section>

          {/* COMMUNITY + PREMIUM (coming soon row) */}
          <div className="pf-soon-row pnp-rise" style={{ '--i': 2 }}>
            <div className="pf-soon">
              <span className="pf-soon-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="9" cy="8.5" r="3.2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3.5 19c.6-3 2.8-4.8 5.5-4.8s4.9 1.8 5.5 4.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M15.5 5.6a3 3 0 0 1 0 5.8M17.5 14.6c1.6.6 2.7 2.1 3 4.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              <p className="pf-soon-title">Community</p>
              <p className="pf-soon-sub">Coming soon</p>
            </div>
            <div className="pf-soon pf-soon-gold">
              <span className="pf-soon-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M4 8.5 8 12l4-6 4 6 4-3.5-1.6 9.5H5.6L4 8.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              </span>
              <p className="pf-soon-title">PNP Premium</p>
              <p className="pf-soon-sub">Coming soon</p>
            </div>
          </div>
        </aside>

        <div className="pf-main">
          {/* TABS */}
          <div className="pnp-segmented pf-tabs" role="tablist">
            {[
              { key: 'career', label: 'Career' },
              { key: 'activity', label: 'Activity' },
              { key: 'settings', label: 'Settings' }
            ].map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={activeTab === tab.key ? 'active' : ''}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* CAREER TAB */}
          {activeTab === 'career' && (
            <div className="pf-tab-body" key="career">
              {/* Pick accuracy stat card */}
              <section className="pf-card pf-accuracy pnp-rise">
                <div className="pf-card-head">
                  <p className="pnp-eyebrow pnp-eyebrow-mute pf-card-eyebrow">Career Accuracy</p>
                  <span className="pf-muted-num">
                    {stats.correctPicks}/{stats.scoredPicks} correct
                  </span>
                </div>
                <p className="pf-accuracy-value">
                  {stats.accuracy}<span>%</span>
                </p>
                <div className="pf-bar pf-bar-win">
                  <div className="pf-bar-fill" style={{ width: `${Math.min(stats.accuracy, 100)}%` }} />
                </div>
              </section>

              {/* Your Pools list */}
              <section className="pf-card pnp-rise" style={{ '--i': 1 }}>
                <div className="pf-card-head">
                  <h3 className="pnp-title pf-card-title">Your Pools</h3>
                  <span className="pf-muted-num">
                    {pools.length} {pools.length === 1 ? 'pool' : 'pools'}
                  </span>
                </div>
                {pools.length === 0 ? (
                  <p className="pf-empty-line">
                    You haven't joined any pools yet. <Link to="/join">Join one</Link>.
                  </p>
                ) : (
                  <div className="pf-rows">
                    {pools.slice(0, 5).map(p => (
                      <Link
                        key={p.id}
                        to={`/pool/${p.id}`}
                        className="pf-row"
                      >
                        <span className="pf-row-mark" aria-hidden="true">{(p.name || '?').trim().charAt(0).toUpperCase()}</span>
                        <div className="pf-row-text">
                          <p className="pf-row-title">{p.name}</p>
                          <p className="pf-row-sub">
                            {p.member_count || 0} {(p.member_count || 0) === 1 ? 'member' : 'members'}
                          </p>
                        </div>
                        <span className="pf-row-chev" aria-hidden="true">›</span>
                      </Link>
                    ))}
                    {pools.length > 5 && (
                      <Link to="/dashboard" className="pf-more-link">
                        View all {pools.length} pools
                      </Link>
                    )}
                  </div>
                )}
              </section>

              {/* Badges - inline grid */}
              <section className="pf-card pf-badges pnp-rise" style={{ '--i': 2 }}>
                <div className="pf-card-head">
                  <p className="pnp-eyebrow pnp-eyebrow-gold pf-card-eyebrow">Badges</p>
                  <Link to="/badges" className="pf-head-link">
                    View all ›
                  </Link>
                </div>

                {/* Earned/Title Shot summary row */}
                <div className="pf-badge-summary">
                  <div className="pf-badge-sum">
                    <p className="pf-badge-sum-value">{totalBadges}</p>
                    <p className="pf-badge-sum-label">Earned</p>
                  </div>
                  <div className="pf-badge-sum pf-badge-sum-gold">
                    <p className="pf-badge-sum-value">{titleShotBadges.length}</p>
                    <p className="pf-badge-sum-label">Title Shots</p>
                  </div>
                </div>

                {/* Inline badges grid - all categories */}
                <div className="pf-medals">
                  {BADGE_CATEGORIES.flatMap(cat => cat.badges).map(badge => {
                    const earned = isEarned(stats.badges, badge.type);
                    const count = badge.multi ? getEarnedCount(stats.badges, badge.type) : 0;
                    return (
                      <div
                        key={badge.type}
                        role="button"
                        tabIndex={0}
                        title={badge.label + (count > 1 ? ` x${count}` : '')}
                        onClick={() => setSelectedBadge({ ...badge, _earned: earned, _count: count })}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedBadge({ ...badge, _earned: earned, _count: count }); } }}
                        className="pf-medal"
                      >
                        <BadgeMedallion badge={badge} earned={earned} count={count} size={52} showLock={false} />
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Previous Events with pool selector */}
              <section className="pf-card pnp-rise" style={{ '--i': 3 }}>
                <div className="pf-card-head pf-card-head-wrap">
                  <h3 className="pnp-title pf-card-title">Previous Events</h3>
                  {pools.length > 0 && (
                    <select
                      value={selectedPoolId}
                      onChange={(e) => setSelectedPoolId(e.target.value)}
                      className="form-select pf-pool-select"
                      aria-label="Pool"
                    >
                      {pools.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {pools.length === 0 ? (
                  <p className="pf-empty-line">
                    Join a pool to see past events.
                  </p>
                ) : poolEventsLoading ? (
                  <div className="pf-rows" aria-label="Loading...">
                    <div className="pnp-skel pf-row-skel" />
                    <div className="pnp-skel pf-row-skel" />
                    <div className="pnp-skel pf-row-skel" />
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : poolEvents.length === 0 ? (
                  <p className="pf-empty-line">
                    No completed events yet for this pool.
                  </p>
                ) : (
                  <div className="pf-rows">
                    {poolEvents.slice(0, 6).map(evt => {
                      const winner = (evt.winners || [])[0];
                      return (
                        <Link
                          key={evt.id}
                          to={`/pool/${selectedPoolId}/leaderboard?event=${evt.id}#historical-standings`}
                          className="pf-row"
                        >
                          <div className="pf-row-text">
                            <p className="pf-row-title pf-row-title-ellipsis">
                              {evt.name}
                            </p>
                            <p className="pf-row-sub pf-row-sub-mono">
                              {evt.date && new Date(evt.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          </div>
                          {winner && (
                            <div className="pf-winner">
                              <p className="pf-winner-name">
                                <img src="/images/title-belt.svg" alt="" aria-hidden="true" />
                                {winner.username}
                              </p>
                              <p className="pf-winner-pts">
                                {winner.total_points} pts
                              </p>
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ACTIVITY TAB */}
          {activeTab === 'activity' && (
            <div className="pf-tab-body" key="activity">
              {nextEvent && (
                <section className="pf-card pf-event pf-event-next pnp-rise">
                  <div className="pnp-claw pf-event-claw" aria-hidden="true" />
                  <p className="pnp-eyebrow pf-card-eyebrow">
                    Upcoming Event
                  </p>
                  <h3 className="pnp-title pf-event-name">{nextEvent.name}</h3>
                  <p className="pf-event-date">
                    {new Date(nextEvent.date).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric'
                    })}
                  </p>
                  <Link to="/dashboard" className="btn btn-primary btn-sm pf-event-cta">
                    Make picks ›
                  </Link>
                </section>
              )}

              {lastEvent && (
                <section className="pf-card pf-event pnp-rise" style={{ '--i': 1 }}>
                  <p className="pnp-eyebrow pnp-eyebrow-mute pf-card-eyebrow">
                    Last Event
                  </p>
                  <h3 className="pnp-title pf-event-name">{lastEvent.name}</h3>
                  <p className="pf-event-date">
                    {new Date(lastEvent.date).toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric'
                    })}
                  </p>
                </section>
              )}

              {!nextEvent && !lastEvent && (
                <div className="pnp-empty pf-empty">
                  <p>No recent activity yet.</p>
                </div>
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div className="pf-tab-body" key="settings">
              <section className="pf-card pf-form-card pnp-rise">
                <h3 className="pnp-title pf-card-title">Account Settings</h3>
                <p className="pf-card-sub">
                  Update your profile information
                </p>

                {error && (
                  <div className="message message-error">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="message message-success">
                    {success}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input pf-input-locked"
                      value={user?.email || ''}
                      disabled
                    />
                  </div>

                  <div className="pf-field-pair">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input type="text" className="form-input" value={firstName}
                        onChange={(e) => setFirstName(e.target.value)} placeholder="First name" required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input type="text" className="form-input" value={lastName}
                        onChange={(e) => setLastName(e.target.value)} placeholder="Last name" required />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Your username"
                      minLength={3}
                      maxLength={20}
                      pattern="[a-zA-Z0-9_]+"
                      title="Only letters, numbers, and underscores allowed"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <select className="form-select" value={country} onChange={(e) => setCountry(e.target.value)}>
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.flag ? `${c.flag} ${c.name}` : c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pf-field-pair">
                    <div className="form-group">
                      <label className="form-label">Instagram</label>
                      <input type="text" className="form-input" value={instagram}
                        onChange={(e) => setInstagram(e.target.value.replace(/^@/, ''))} placeholder="username" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">X (Twitter)</label>
                      <input type="text" className="form-input" value={xHandle}
                        onChange={(e) => setXHandle(e.target.value.replace(/^@/, ''))} placeholder="username" />
                    </div>
                  </div>

                  {!HIDE_MONEY && (
                  <div className="form-group">
                    <label className="form-label">Venmo Handle</label>
                    <input type="text" className="form-input" value={venmoHandle}
                      onChange={(e) => setVenmoHandle(e.target.value)} placeholder="@yourhandle" />
                    <p className="form-hint">
                      Used for pool payouts if you're a commissioner
                    </p>
                  </div>
                  )}

                  <button type="submit" className="btn btn-primary btn-lg btn-block pf-save" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </section>

              {/* Notifications */}
              <section className="pf-card pf-form-card pnp-rise" style={{ '--i': 1 }}>
                <h3 className="pnp-title pf-card-title pf-card-title-sm">Notifications</h3>
                <label className="pf-switch-row">
                  <div className="pf-switch-text">
                    <div className="pf-switch-title">Corner Chat sounds</div>
                    <div className="pf-switch-sub">
                      Soft chime + light haptic when a new message arrives in any of your pools.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="pnp-switch"
                    role="switch"
                    aria-checked={chatAlertsOn}
                    checked={chatAlertsOn}
                    onChange={() => {
                      const next = !chatAlertsOn;
                      setChatAlertsOn(next);
                      setChatAlertsMuted(!next);
                    }}
                  />
                </label>
              </section>

              <div className="pf-danger-zone pnp-rise" style={{ '--i': 2 }}>
                <button
                  onClick={async () => { await logout(); window.location.href = '/'; }}
                  className="btn btn-ghost btn-block pf-logout"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M14 4h3.5A1.5 1.5 0 0 1 19 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14M10 8l-4 4 4 4M6 12h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Log Out
                </button>

                {/* Danger zone — Delete account */}
                <div className="pf-delete">
                  <h3 className="pf-delete-title">Delete Account</h3>
                  <p className="pf-delete-text">
                    Permanently removes your profile, email, and personal info. Your past picks and pool standings
                    stay so other members' history isn't broken — you'll just appear as "Deleted user." This
                    can't be undone.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setDeleteOpen(true); setDeleteConfirmText(''); setDeleteError(''); }}
                    className="btn btn-danger btn-block"
                  >
                    Delete my account
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation modal (portaled so it stacks above the dock) */}
      {deleteOpen && ReactDOM.createPortal(
        <div
          role="dialog"
          aria-modal="true"
          className="modal-backdrop pf-modal-backdrop"
        >
          <div className="pf-modal-scrim"
               onClick={() => !deleting && setDeleteOpen(false)} />
          <div className="modal-card pf-modal">
            <div className="pf-modal-icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M12 8v5M12 16.5v.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
                <path d="M10.3 3.9 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="pnp-title pf-modal-title">Delete your account?</h2>
            <p className="pf-modal-text">
              This will scrub your profile, email, and personal info, and delete your chat messages.
              Your picks and pool history will be retained but anonymized. You won't be able to log
              back in. <strong>This can't be undone.</strong>
            </p>
            <label className="pf-modal-label">
              Type your username (<strong>{user?.username}</strong>) to confirm:
            </label>
            <input
              type="text"
              autoFocus
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="form-input"
              placeholder={user?.username}
              disabled={deleting}
            />
            {deleteError && (
              <div className="pf-modal-error">{deleteError}</div>
            )}
            <div className="pf-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== user?.username}
                className="btn pf-delete-confirm"
              >
                {deleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
    {selectedBadge && (
      <BadgeDetailModal
        badge={selectedBadge}
        earned={selectedBadge._earned}
        count={selectedBadge._count}
        progress={selectedBadge._earned ? null : getBadgeProgress(selectedBadge, stats)}
        onClose={() => setSelectedBadge(null)}
      />
    )}
    </PullToRefresh>
  );
}

export default Profile;
